import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { FaPlus, FaEdit, FaTrash, FaToggleOn, FaToggleOff } from 'react-icons/fa';
import { useTitle } from '../../../context/TitleContext';
import ConfirmModal from '../../../shared/components/ConfirmModal';
import {
    fetchClinicBranches,
    addClinicBranch,
    updateClinicBranch,
    updateClinicBranchStatus,
    deleteClinicBranch,
    clearClinicBranchesError
} from '../../../redux/Slices/ClinicBranchesSlice';
import { showSuccessToast, showErrorToast } from '../../../shared/utils/toastHelper';

const initialFormState = {
    title_ar: '',
    title_en: ''
};

const getBranchId = (branch) =>
    branch?.id ??
    branch?.branch_id ??
    branch?.clinic_branch_id ??
    branch?.branchId ??
    null;

const getStatusValue = (branch) => {
    if (!branch) return 0;
    const possible = branch.status ?? branch.is_active ?? branch.active ?? branch.enabled ?? 0;
    const numeric = Number(possible);
    if (Number.isNaN(numeric)) {
        if (String(possible).toLowerCase() === 'active') return 1;
        if (String(possible).toLowerCase() === 'inactive') return 0;
        return possible ? 1 : 0;
    }
    return numeric;
};

const ClinicBranches = () => {
    const { setTitle } = useTitle();
    const dispatch = useDispatch();

    const { branches, loading, error } = useSelector((state) => state.clinicBranches);

    const [formState, setFormState] = useState(initialFormState);
    const [modalState, setModalState] = useState({ open: false, mode: 'add', branch: null });
    const [saving, setSaving] = useState(false);
    const [togglingId, setTogglingId] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, data: null });

    useEffect(() => {
        setTitle('فروع العيادة');
        dispatch(fetchClinicBranches());
    }, [setTitle, dispatch]);

    useEffect(() => {
        if (error) {
            showErrorToast(error);
            dispatch(clearClinicBranchesError());
        }
    }, [error, dispatch]);

    const preparedBranches = useMemo(() => {
        if (!Array.isArray(branches)) return [];
        return branches.map((branch) => ({
            ...branch,
            id: getBranchId(branch),
            statusValue: getStatusValue(branch),
            titleArabic: branch?.title_ar ?? branch?.titleAr ?? branch?.title ?? branch?.name_ar ?? branch?.name ?? '',
            titleEnglish: branch?.title_en ?? branch?.titleEn ?? branch?.title_en ?? branch?.name_en ?? '',
            createdAt: branch?.created_at ?? branch?.createdAt ?? branch?.createdAtFormatted ?? null
        }));
    }, [branches]);

    const handleOpenModal = (mode = 'add', branch = null) => {
        if (mode === 'edit' && branch) {
            setFormState({
                title_ar: branch.titleArabic || '',
                title_en: branch.titleEnglish || ''
            });
        } else {
            setFormState(initialFormState);
        }
        setModalState({ open: true, mode, branch });
    };

    const handleCloseModal = () => {
        setModalState({ open: false, mode: 'add', branch: null });
        setFormState(initialFormState);
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormState((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSaveBranch = async (e) => {
        e.preventDefault();
        if (!formState.title_ar.trim() || !formState.title_en.trim()) {
            showErrorToast('يرجى إدخال اسم الفرع بالعربية والإنجليزية');
            return;
        }

        const payload = {
            title_ar: formState.title_ar.trim(),
            title_en: formState.title_en.trim()
        };

        setSaving(true);
        try {
            if (modalState.mode === 'edit' && modalState.branch) {
                const branchId = modalState.branch.id;
                if (!branchId) {
                    throw new Error('معرف الفرع غير موجود');
                }
                const response = await dispatch(
                    updateClinicBranch({ id: branchId, data: payload })
                ).unwrap();
                showSuccessToast(response?.message || 'تم تحديث الفرع بنجاح');
            } else {
                const response = await dispatch(addClinicBranch(payload)).unwrap();
                showSuccessToast(response?.message || 'تم إضافة الفرع بنجاح');
            }
            handleCloseModal();
            dispatch(fetchClinicBranches());
        } catch (err) {
            showErrorToast(err || 'حدث خطأ غير متوقع');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleStatus = async (branch) => {
        const branchId = branch?.id;
        if (!branchId) {
            showErrorToast('لا يمكن تحديد هذا الفرع');
            return;
        }
        const nextStatus = branch.statusValue === 1 ? 0 : 1;
        setTogglingId(branchId);
        try {
            const response = await dispatch(
                updateClinicBranchStatus({ id: branchId, status: nextStatus })
            ).unwrap();
            showSuccessToast(response?.message || 'تم تغيير حالة الفرع');
            dispatch(fetchClinicBranches());
        } catch (err) {
            showErrorToast(err || 'فشل في تغيير حالة الفرع');
        } finally {
            setTogglingId(null);
        }
    };

    const handleDeleteBranch = async () => {
        const branch = confirmModal.data;
        const branchId = getBranchId(branch);
        if (!branchId) {
            showErrorToast('لا يمكن تحديد هذا الفرع');
            return;
        }
        setDeletingId(branchId);
        try {
            const response = await dispatch(deleteClinicBranch(branchId)).unwrap();
            showSuccessToast(response?.message || 'تم حذف الفرع بنجاح');
            dispatch(fetchClinicBranches());
        } catch (err) {
            showErrorToast(err || 'فشل في حذف الفرع');
        } finally {
            setDeletingId(null);
            setConfirmModal({ isOpen: false, data: null });
        }
    };

    return (
        <div className="main_content clinic-branches-page">
            <div className="dashboard-container">
                <div className="d-flex justify-content-end align-items-center mb-4">
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="btn btn-primary d-flex align-items-center gap-2"
                        onClick={() => handleOpenModal('add')}
                        style={{
                            backgroundColor: '#184194',
                            borderColor: '#184194',
                            borderRadius: '8px'
                        }}
                    >
                        <FaPlus />
                        إضافة فرع جديد
                    </motion.button>
                </div>

                <div className="card shadow-lg border-0">
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">جاري التحميل...</span>
                                </div>
                            </div>
                        ) : preparedBranches.length === 0 ? (
                            <div className="text-center py-5">
                                <p className="text-muted mb-0">لا توجد فروع مضافة حتى الآن</p>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-modern">
                                    <thead>
                                        <tr>
                                            <th className="text-center">#</th>
                                            <th className="text-center">الاسم (عربي)</th>
                                            <th className="text-center">الاسم (إنجليزي)</th>
                                            <th className="text-center">الحالة</th>
                                            <th className="text-center">تاريخ الإضافة</th>
                                            <th className="text-center">الإجراءات</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {preparedBranches.map((branch, index) => (
                                            <motion.tr
                                                key={branch.id ?? index}
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: index * 0.05 }}
                                                className="table-row-modern"
                                            >
                                                <td className="text-center">
                                                    <div className="order-number-badge">
                                                        #{index + 1}
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <div className="branch-title">{branch.titleArabic || '-'}</div>
                                                </td>
                                                <td className="text-center">
                                                    <div className="branch-title">{branch.titleEnglish || '-'}</div>
                                                </td>
                                                <td className="text-center">
                                                    <span
                                                        className={`badge branch-status-badge ${branch.statusValue === 1 ? 'branch-status-active' : 'branch-status-inactive'}`}
                                                    >
                                                        {branch.statusValue === 1 ? 'نشط' : 'غير نشط'}
                                                    </span>
                                                </td>
                                                <td className="text-center text-muted">
                                                    {branch.createdAt
                                                        ? new Date(branch.createdAt).toLocaleDateString('ar-EG')
                                                        : '-'}
                                                </td>
                                                <td className="text-center">
                                                    <div className="action-buttons d-flex gap-2 justify-content-center flex-wrap">
                                                        <motion.button
                                                            whileHover={{ scale: 1.05 }}
                                                            whileTap={{ scale: 0.95 }}
                                                            className="btn btn-sm action-btn action-btn-edit"
                                                            onClick={() => handleOpenModal('edit', branch)}
                                                            title="تعديل"
                                                        >
                                                            <FaEdit />
                                                        </motion.button>
                                                        <motion.button
                                                            whileHover={{ scale: 1.05 }}
                                                            whileTap={{ scale: 0.95 }}
                                                            className={`btn btn-sm action-btn ${branch.statusValue === 1 ? 'action-btn-disable' : 'action-btn-enable'}`}
                                                            onClick={() => handleToggleStatus(branch)}
                                                            disabled={togglingId === branch.id}
                                                            title={branch.statusValue === 1 ? 'إلغاء التفعيل' : 'تفعيل'}
                                                        >
                                                            {togglingId === branch.id ? (
                                                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                            ) : branch.statusValue === 1 ? (
                                                                <FaToggleOff />
                                                            ) : (
                                                                <FaToggleOn />
                                                            )}
                                                        </motion.button>
                                                        <motion.button
                                                            whileHover={{ scale: 1.05 }}
                                                            whileTap={{ scale: 0.95 }}
                                                            className="btn btn-sm action-btn action-btn-delete"
                                                            onClick={() =>
                                                                setConfirmModal({
                                                                    isOpen: true,
                                                                    data: branch
                                                                })
                                                            }
                                                            disabled={deletingId === branch.id}
                                                            title="حذف"
                                                        >
                                                            {deletingId === branch.id ? (
                                                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                            ) : (
                                                                <FaTrash />
                                                            )}
                                                        </motion.button>
                                                    </div>
                                                </td>
                                            </motion.tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Add / Edit Modal */}
            {modalState.open && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title mb-0">
                                    {modalState.mode === 'edit' ? 'تعديل بيانات الفرع' : 'إضافة فرع جديد'}
                                </h5>
                                <button type="button" className="btn-close" onClick={handleCloseModal}></button>
                            </div>
                            <form onSubmit={handleSaveBranch}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">اسم الفرع (عربي)</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="title_ar"
                                            value={formState.title_ar}
                                            onChange={handleFormChange}
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">اسم الفرع (إنجليزي)</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="title_en"
                                            value={formState.title_en}
                                            onChange={handleFormChange}
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={handleCloseModal} disabled={saving}>
                                        إلغاء
                                    </button>
                                    <button type="submit" className="btn btn-primary" disabled={saving}>
                                        {saving ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                جاري الحفظ...
                                            </>
                                        ) : (
                                            'حفظ'
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            <ConfirmModal
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal({ isOpen: false, data: null })}
                onConfirm={handleDeleteBranch}
                title="تأكيد الحذف"
                message="هل أنت متأكد من حذف هذا الفرع؟"
                confirmText="نعم، حذف"
                cancelText="إلغاء"
                confirmButtonColor="#dc3545"
                type="danger"
            />

            <style jsx>{`
                .clinic-branches-page .branch-title {
                    font-weight: 600;
                    color: #1f2937;
                }

                .clinic-branches-page .branch-status-badge {
                    min-width: 80px;
                    font-size: 0.8rem;
                    color: #fff !important;
                    font-weight: 600;
                }

                .clinic-branches-page .branch-status-active {
                    background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
                }

                .clinic-branches-page .branch-status-inactive {
                    background: linear-gradient(135deg, #9ca3af 0%, #6b7280 100%);
                }

                .clinic-branches-page .action-buttons .action-btn {
                    border-radius: 8px;
                    min-width: 44px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    border: none;
                    color: #fff !important;
                    transition: all 0.2s ease-in-out;
                    box-shadow: 0 4px 12px rgba(24, 65, 148, 0.15);
                }

                .clinic-branches-page .action-buttons .action-btn svg {
                    color: inherit;
                }

                .clinic-branches-page .action-buttons .action-btn-edit {
                    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
                }

                .clinic-branches-page .action-buttons .action-btn-enable {
                    background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
                }

                .clinic-branches-page .action-buttons .action-btn-disable {
                    background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
                }

                .clinic-branches-page .action-buttons .action-btn-delete {
                    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
                }

                .clinic-branches-page .action-buttons .action-btn:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 6px 16px rgba(24, 65, 148, 0.2);
                }

                .clinic-branches-page .action-buttons .action-btn:disabled {
                    opacity: 0.75;
                    cursor: not-allowed;
                    box-shadow: none;
                }

                .clinic-branches-page .modal-content {
                    border-radius: 12px;
                    border: none;
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
                }

                .clinic-branches-page .modal-header {
                    background: linear-gradient(135deg, #184194 0%, #1e40af 100%);
                    color: #fff;
                    border-radius: 12px 12px 0 0;
                    border-bottom: none;
                }

                .clinic-branches-page .modal-header .btn-close {
                    filter: invert(1);
                }

                .clinic-branches-page .modal-footer {
                    border-top: none;
                }
            `}</style>
        </div>
    );
};

export default ClinicBranches;

